/**
 * Planificateur de réponses pour les agents
 * Gère les taux de réussite par niveau et la variation des réponses
 */

/**
 * Configurations des niveaux d'agent
 */
const AGENT_LEVEL_CONFIG = {
  'A': {
    name: 'Fort',
    successRateMin: 85,
    successRateMax: 100,
    description: '100%-85% correct'
  },
  'B': {
    name: 'Moyen', 
    successRateMin: 40,
    successRateMax: 84,
    description: '84%-40% correct'
  },
  'C': {
    name: 'Faible',
    successRateMin: 0,
    successRateMax: 39,
    description: '<40% correct'
  }
}

/**
 * Détermine si l'agent doit donner une réponse correcte
 * @param {string} agentLevel - Niveau de l'agent (A, B, ou C)
 * @param {number} variationCoeff - Coefficient de variation (pour futures campagnes)
 * @returns {boolean} True si l'agent doit répondre correctement
 */
export function shouldAnswerCorrectly(agentLevel, variationCoeff = 1.0) {
  const config = AGENT_LEVEL_CONFIG[agentLevel]
  if (!config) {
    console.warn(`Niveau d'agent inconnu: ${agentLevel}, utilisation du niveau C par défaut`)
    return Math.random() < 0.2 // Niveau C par défaut
  }
  
  // Calculer le taux de réussite pour ce niveau
  const baseSuccessRate = (config.successRateMin + config.successRateMax) / 2
  
  // Appliquer le coefficient de variation (pour l'instant fixé à 1.0)
  // Dans le futur, ce coefficient pourra varier pour les campagnes
  const adjustedSuccessRate = Math.min(100, Math.max(0, baseSuccessRate * variationCoeff))
  
  // Décision aléatoire basée sur le taux de réussite
  const randomValue = Math.random() * 100
  const shouldBeCorrect = randomValue <= adjustedSuccessRate
  
  console.log(`🎯 [Agent ${agentLevel}] Taux: ${adjustedSuccessRate.toFixed(1)}%, Random: ${randomValue.toFixed(1)}%, Correct: ${shouldBeCorrect}`)
  
  return shouldBeCorrect
}

/**
 * Génère une réponse pour une interaction de type choix
 * @param {Object} options - Les options de l'interaction 
 * @param {boolean} isMultiple - Si c'est un choix multiple
 * @param {string} agentLevel - Niveau de l'agent
 * @param {number} variationCoeff - Coefficient de variation
 * @returns {string|Array} La réponse générée
 */
export function generateChoiceResponse(options, isMultiple, agentLevel, variationCoeff = 1.0) {
  if (!options || options.length === 0) {
    console.warn('Aucune option disponible pour générer une réponse')
    return isMultiple ? [] : null
  }
  
  const correctOptions = options.filter(option => option.isCorrect)
  const incorrectOptions = options.filter(option => !option.isCorrect)
  const shouldBeCorrect = shouldAnswerCorrectly(agentLevel, variationCoeff)
  
  if (isMultiple) {
    // Choix multiple
    if (shouldBeCorrect && correctOptions.length > 0) {
      // Réponse correcte : sélectionner toutes les bonnes réponses
      return correctOptions.map(option => option.id)
    } else if (incorrectOptions.length > 0) {
      // Réponse incorrecte : sélectionner une mauvaise réponse au hasard
      const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)]
      return [randomIncorrect.id]
    }
    return []
  } else {
    // Choix simple
    if (shouldBeCorrect && correctOptions.length > 0) {
      // Réponse correcte : sélectionner une bonne réponse au hasard
      const randomCorrect = correctOptions[Math.floor(Math.random() * correctOptions.length)]
      return randomCorrect.id
    } else if (incorrectOptions.length > 0) {
      // Réponse incorrecte : sélectionner une mauvaise réponse au hasard
      const randomIncorrect = incorrectOptions[Math.floor(Math.random() * incorrectOptions.length)]
      return randomIncorrect.id
    }
    return null
  }
}

/**
 * Récupère la configuration de l'agent depuis localStorage
 * @returns {Object} Configuration de l'agent
 */
export function getAgentConfig() {
  try {
    const saved = localStorage.getItem('specialAgentConfig')
    if (saved) {
      const config = JSON.parse(saved)
      return {
        level: config.level || 'A',
        executionMode: config.executionMode || 'generate',
        variationCoeff: config.variationCoeff || 1.0
      }
    }
  } catch (error) {
    console.warn('Erreur lors du chargement de la configuration agent:', error)
  }
  
  // Configuration par défaut
  return {
    level: 'A',
    executionMode: 'generate',
    variationCoeff: 1.0
  }
}

/**
 * Obtient la configuration d'un niveau d'agent
 * @param {string} level - Niveau de l'agent
 * @returns {Object} Configuration du niveau
 */
export function getAgentLevelConfig(level) {
  return AGENT_LEVEL_CONFIG[level] || AGENT_LEVEL_CONFIG['C']
}

/**
 * Logs de debugging pour le planificateur
 * @param {string} interactionType - Type d'interaction 
 * @param {string} agentLevel - Niveau de l'agent
 * @param {*} response - Réponse générée
 * @param {boolean} isCorrect - Si la réponse est correcte
 */
export function logAgentResponse(interactionType, agentLevel, response, isCorrect) {
  const config = AGENT_LEVEL_CONFIG[agentLevel]
  console.log(`🤖 [${interactionType}] Agent ${agentLevel} (${config?.name || 'Inconnu'}): ${isCorrect ? '✅' : '❌'} - ${JSON.stringify(response)}`)
}

// Export de la configuration pour utilisation externe
export { AGENT_LEVEL_CONFIG }